import React from 'react';
import { Button, Alert, TextField, Card, CardContent, CardHeader } from '@mui/material';

interface ColorPaletteTesterProps {
  paletteName?: string;
  className?: string;
}

const ColorPaletteTester: React.FC<ColorPaletteTesterProps> = ({ 
  paletteName = "Current Palette", 
  className = "" 
}) => {
  const colorGroups = [
    {
      name: "Primary Colors",
      colors: [
        { name: "50", class: "bg-primary-50 text-primary-900" },
        { name: "100", class: "bg-primary-100 text-primary-900" },
        { name: "500", class: "bg-primary-500 text-white" },
        { name: "600", class: "bg-primary-600 text-white" },
        { name: "900", class: "bg-primary-900 text-white" },
      ]
    },
    {
      name: "Secondary Colors",
      colors: [
        { name: "50", class: "bg-secondary-50 text-secondary-900" },
        { name: "100", class: "bg-secondary-100 text-secondary-900" },
        { name: "500", class: "bg-secondary-500 text-white" },
        { name: "600", class: "bg-secondary-600 text-white" },
        { name: "900", class: "bg-secondary-900 text-white" },
      ]
    },
    {
      name: "Semantic Colors",
      colors: [
        { name: "Success", class: "bg-success text-white" },
        { name: "Warning", class: "bg-warning text-white" },
        { name: "Error", class: "bg-error text-white" },
        { name: "Info", class: "bg-info text-white" },
      ]
    }
  ];

  const componentExamples = [
    {
      name: "Admin Navigation Bar",
      component: (
        <div className="bg-primary-500 text-white p-4 rounded-lg">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <h2 className="text-xl font-bold">Admin Dashboard</h2>
              <nav className="flex space-x-2">
                <button className="px-3 py-1 bg-primary-600 hover:bg-primary-700 rounded text-sm">Dashboard</button>
                <button className="px-3 py-1 bg-primary-600 hover:bg-primary-700 rounded text-sm">Orders</button>
                <button className="px-3 py-1 bg-primary-600 hover:bg-primary-700 rounded text-sm">Products</button>
                <button className="px-3 py-1 bg-primary-600 hover:bg-primary-700 rounded text-sm">Settings</button>
              </nav>
            </div>
            <div className="flex items-center space-x-2">
              <span className="text-sm">Admin User</span>
              <div className="w-8 h-8 bg-primary-600 rounded-full flex items-center justify-center text-sm font-bold">AU</div>
            </div>
          </div>
        </div>
      )
    },
    {
      name: "Admin Stat Tiles Grid",
      component: (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="bg-white p-4 rounded-lg shadow-lg border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 font-medium">Total Orders</p>
                <p className="text-2xl font-bold text-primary-600">1,234</p>
              </div>
              <div className="w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center">
                <span className="text-primary-600 text-xl">📦</span>
              </div>
            </div>
          </div>
          <div className="bg-white p-4 rounded-lg shadow-lg border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 font-medium">Active Products</p>
                <p className="text-2xl font-bold text-secondary-600">89</p>
              </div>
              <div className="w-12 h-12 bg-secondary-100 rounded-lg flex items-center justify-center">
                <span className="text-secondary-600 text-xl">🛍️</span>
              </div>
            </div>
          </div>
          <div className="bg-white p-4 rounded-lg shadow-lg border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 font-medium">Revenue</p>
                <p className="text-2xl font-bold text-success">$12,345</p>
              </div>
              <div className="w-12 h-12 bg-success-100 rounded-lg flex items-center justify-center">
                <span className="text-success text-xl">💰</span>
              </div>
            </div>
          </div>
          <div className="bg-white p-4 rounded-lg shadow-lg border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 font-medium">Volunteers</p>
                <p className="text-2xl font-bold text-warning">45</p>
              </div>
              <div className="w-12 h-12 bg-warning-100 rounded-lg flex items-center justify-center">
                <span className="text-warning text-xl">👥</span>
              </div>
            </div>
          </div>
        </div>
      )
    },
    {
      name: "Main App Navigation",
      component: (
        <div className="bg-white shadow-sm border-b border-gray-200 p-4 rounded-lg">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-6">
              <h1 className="text-xl font-bold text-primary-600">New Life Foodbarn</h1>
              <nav className="hidden md:flex space-x-4">
                <a href="#" className="text-gray-700 hover:text-primary-600 font-medium">Home</a>
                <a href="#" className="text-gray-700 hover:text-primary-600 font-medium">Food Parcels</a>
                <a href="#" className="text-gray-700 hover:text-primary-600 font-medium">Sponsors</a>
                <a href="#" className="text-gray-700 hover:text-primary-600 font-medium">Volunteer</a>
                <a href="#" className="text-gray-700 hover:text-primary-600 font-medium">Donations</a>
              </nav>
            </div>
            <div className="flex items-center space-x-2">
              <button className="btn btn-primary">Order Now</button>
              <button className="btn btn-outline">Login</button>
            </div>
          </div>
        </div>
      )
    },
    {
      name: "Food Parcel Cards",
      component: (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <div className="card hover:shadow-lg transition-shadow">
            <div className="card-header">
              <h3 className="card-title text-lg font-semibold">Standard Food Parcel</h3>
              <p className="text-gray-600 text-sm">Perfect for small families</p>
            </div>
            <div className="card-body">
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Fresh Vegetables</span>
                  <span className="text-sm font-medium">✓</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Bread & Bakery</span>
                  <span className="text-sm font-medium">✓</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Dairy Products</span>
                  <span className="text-sm font-medium">✓</span>
                </div>
              </div>
              <div className="mt-4 pt-4 border-t border-gray-200">
                <div className="flex justify-between items-center">
                  <span className="text-2xl font-bold text-primary-600">$25.00</span>
                  <button className="btn btn-primary btn-sm">Order Now</button>
                </div>
              </div>
            </div>
          </div>
          <div className="card hover:shadow-lg transition-shadow">
            <div className="card-header">
              <h3 className="card-title text-lg font-semibold">Family Food Parcel</h3>
              <p className="text-gray-600 text-sm">Ideal for larger families</p>
            </div>
            <div className="card-body">
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Fresh Vegetables</span>
                  <span className="text-sm font-medium">✓</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Bread & Bakery</span>
                  <span className="text-sm font-medium">✓</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Dairy Products</span>
                  <span className="text-sm font-medium">✓</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Meat & Protein</span>
                  <span className="text-sm font-medium">✓</span>
                </div>
              </div>
              <div className="mt-4 pt-4 border-t border-gray-200">
                <div className="flex justify-between items-center">
                  <span className="text-2xl font-bold text-primary-600">$45.00</span>
                  <button className="btn btn-primary btn-sm">Order Now</button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )
    },
    {
      name: "Data Table",
      component: (
        <div className="bg-white rounded-lg shadow border border-gray-200 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
            <h3 className="text-lg font-semibold text-gray-900">Recent Orders</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="table w-full">
              <thead>
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Order #</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Customer</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Total</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                <tr className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">#10035</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">John Smith</td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-success-100 text-success-800">
                      Completed
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">$45.00</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <button className="text-primary-600 hover:text-primary-900 mr-3">View</button>
                    <button className="text-gray-600 hover:text-gray-900">Edit</button>
                  </td>
                </tr>
                <tr className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">#10036</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">Jane Doe</td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-warning-100 text-warning-800">
                      Pending
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">$32.50</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <button className="text-primary-600 hover:text-primary-900 mr-3">View</button>
                    <button className="text-gray-600 hover:text-gray-900">Edit</button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      )
    },
    {
      name: "Alert Messages",
      component: (
        <div className="space-y-4">
          <div className="bg-success-50 border border-success-200 rounded-lg p-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <span className="text-success-400 text-xl">✓</span>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-success-800">Success!</h3>
                <p className="text-sm text-success-700">Your order has been placed successfully.</p>
              </div>
            </div>
          </div>
          <div className="bg-warning-50 border border-warning-200 rounded-lg p-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <span className="text-warning-400 text-xl">⚠</span>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-warning-800">Warning</h3>
                <p className="text-sm text-warning-700">Please review your order before submitting.</p>
              </div>
            </div>
          </div>
          <div className="bg-error-50 border border-error-200 rounded-lg p-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <span className="text-error-400 text-xl">✗</span>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-error-800">Error</h3>
                <p className="text-sm text-error-700">There was a problem processing your request.</p>
              </div>
            </div>
          </div>
        </div>
      )
    },
    {
      name: "Form Elements",
      component: (
        <div className="bg-white p-6 rounded-lg shadow border border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Contact Information</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">First Name</label>
              <input type="text" className="form-input w-full" placeholder="Enter first name" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Last Name</label>
              <input type="text" className="form-input w-full" placeholder="Enter last name" />
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
              <input type="email" className="form-input w-full" placeholder="Enter email address" />
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">Message</label>
              <textarea className="form-textarea w-full" rows={3} placeholder="Enter your message"></textarea>
            </div>
            <div className="md:col-span-2">
              <div className="flex items-center">
                <input type="checkbox" className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded" />
                <label className="ml-2 block text-sm text-gray-700">I agree to the terms and conditions</label>
              </div>
            </div>
            <div className="md:col-span-2">
              <button className="btn btn-primary">Submit</button>
              <button className="btn btn-outline ml-2">Cancel</button>
            </div>
          </div>
        </div>
      )
    },
    {
      name: "Hero Section",
      component: (
        <div className="bg-gradient-to-r from-primary-500 to-secondary-500 text-white p-8 rounded-lg">
          <div className="text-center">
            <h1 className="text-4xl font-bold mb-4">Welcome to New Life Foodbarn</h1>
            <p className="text-xl mb-6 opacity-90">Supporting our Gold Coast community with affordable fresh food, love, and hope since 2008.</p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button className="btn bg-white text-primary-600 hover:bg-gray-100 px-6 py-3 rounded-lg font-semibold">
                Order Food Parcel
              </button>
              <button className="btn border-2 border-white text-white hover:bg-white hover:text-primary-600 px-6 py-3 rounded-lg font-semibold">
                Learn More
              </button>
            </div>
          </div>
        </div>
      )
    },
    {
      name: "Sidebar Menu",
      component: (
        <div className="bg-white rounded-lg shadow border border-gray-200 p-4">
          <div className="space-y-2">
            <div className="px-3 py-2 bg-primary-100 text-primary-700 rounded-lg font-medium">
              📊 Dashboard
            </div>
            <div className="px-3 py-2 text-gray-700 hover:bg-gray-100 rounded-lg cursor-pointer">
              📦 Orders
            </div>
            <div className="px-3 py-2 text-gray-700 hover:bg-gray-100 rounded-lg cursor-pointer">
              🛍️ Products
            </div>
            <div className="px-3 py-2 text-gray-700 hover:bg-gray-100 rounded-lg cursor-pointer">
              📊 Categories
            </div>
            <div className="px-3 py-2 text-gray-700 hover:bg-gray-100 rounded-lg cursor-pointer">
              👥 Volunteers
            </div>
            <div className="px-3 py-2 text-gray-700 hover:bg-gray-100 rounded-lg cursor-pointer">
              ⚙️ Settings
            </div>
          </div>
        </div>
      )
    },
    {
      name: "Order Configuration Tiles",
      component: (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <div className="bg-white p-4 rounded-lg shadow border border-gray-200">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-lg font-semibold text-gray-900">Bread Selection</h3>
              <span className="text-sm text-gray-500">Required</span>
            </div>
            <div className="space-y-2">
              <div className="flex items-center justify-between p-2 border border-gray-200 rounded hover:bg-gray-50">
                <span className="text-sm">White Bread</span>
                <span className="text-sm font-medium text-primary-600">$0.00</span>
              </div>
              <div className="flex items-center justify-between p-2 border border-gray-200 rounded hover:bg-gray-50">
                <span className="text-sm">Whole Wheat Bread</span>
                <span className="text-sm font-medium text-primary-600">$0.00</span>
              </div>
            </div>
          </div>
          <div className="bg-white p-4 rounded-lg shadow border border-gray-200">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-lg font-semibold text-gray-900">Grocery Items</h3>
              <span className="text-sm text-gray-500">Optional</span>
            </div>
            <div className="space-y-2">
              <div className="flex items-center justify-between p-2 border border-gray-200 rounded hover:bg-gray-50">
                <span className="text-sm">Rice (1kg)</span>
                <span className="text-sm font-medium text-primary-600">$0.00</span>
              </div>
              <div className="flex items-center justify-between p-2 border border-gray-200 rounded hover:bg-gray-50">
                <span className="text-sm">Pasta (500g)</span>
                <span className="text-sm font-medium text-primary-600">$0.00</span>
              </div>
            </div>
          </div>
          <div className="bg-white p-4 rounded-lg shadow border border-gray-200">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-lg font-semibold text-gray-900">Order Summary</h3>
              <span className="text-sm text-gray-500">Total</span>
            </div>
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span>Food Parcel</span>
                <span>$25.00</span>
              </div>
              <div className="flex justify-between text-sm">
                <span>Delivery Fee</span>
                <span>$30.00</span>
              </div>
              <div className="border-t border-gray-200 pt-2">
                <div className="flex justify-between font-semibold">
                  <span>Total</span>
                  <span className="text-primary-600">$55.00</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )
    },
    {
      name: "Parcel Day Info Cards",
      component: (
        <div className="space-y-4">
          <div className="bg-primary-50 border border-primary-200 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-primary-800">Next Food Parcel Day</h3>
                <p className="text-primary-700">Wednesday, September 11, 2025</p>
                <p className="text-sm text-primary-600">Pickup: 9:00 AM - 12:00 PM</p>
              </div>
              <div className="text-right">
                <div className="text-2xl font-bold text-primary-600">3</div>
                <div className="text-sm text-primary-700">days left</div>
              </div>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-warning-50 border border-warning-200 rounded-lg p-4">
              <div className="flex items-center">
                <span className="text-warning-600 text-xl mr-3">⚠️</span>
                <div>
                  <h4 className="font-semibold text-warning-800">Order Cut-off</h4>
                  <p className="text-sm text-warning-700">Orders close 24 hours before pickup</p>
                </div>
              </div>
            </div>
            <div className="bg-success-50 border border-success-200 rounded-lg p-4">
              <div className="flex items-center">
                <span className="text-success-600 text-xl mr-3">👥</span>
                <div>
                  <h4 className="font-semibold text-success-800">Volunteers Needed</h4>
                  <p className="text-sm text-success-700">Help us serve the community</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )
    },
    {
      name: "Checkout Form Layout",
      component: (
        <div className="max-w-4xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <div className="space-y-6">
              <div className="bg-white p-6 rounded-lg shadow border border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Delivery Information</h3>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Full Name</label>
                    <input type="text" className="form-input w-full" placeholder="Enter your full name" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Address</label>
                    <textarea className="form-textarea w-full" rows={3} placeholder="Enter your address"></textarea>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Suburb</label>
                      <input type="text" className="form-input w-full" placeholder="Suburb" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Postcode</label>
                      <input type="text" className="form-input w-full" placeholder="1234" />
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-white p-6 rounded-lg shadow border border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Payment Method</h3>
                <div className="space-y-3">
                  <label className="flex items-center p-3 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer">
                    <input type="radio" name="payment" className="text-primary-600" />
                    <span className="ml-3">Direct Deposit</span>
                  </label>
                  <label className="flex items-center p-3 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer">
                    <input type="radio" name="payment" className="text-primary-600" />
                    <span className="ml-3">Cash on Delivery</span>
                  </label>
                </div>
              </div>
            </div>
            <div className="space-y-6">
              <div className="bg-white p-6 rounded-lg shadow border border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Order Summary</h3>
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span>Standard Food Parcel</span>
                    <span>$25.00</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Delivery Fee</span>
                    <span>$30.00</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Voucher Applied</span>
                    <span className="text-success">-$10.00</span>
                  </div>
                  <div className="border-t border-gray-200 pt-3">
                    <div className="flex justify-between text-lg font-semibold">
                      <span>Total Amount Payable</span>
                      <span className="text-primary-600">$45.00</span>
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-white p-6 rounded-lg shadow border border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Terms & Conditions</h3>
                <div className="space-y-3">
                  <div className="flex items-start">
                    <input type="checkbox" className="mt-1 text-primary-600" />
                    <span className="ml-2 text-sm text-gray-700">
                      I agree to the terms and conditions and understand the delivery policy.
                    </span>
                  </div>
                  <button className="w-full btn btn-primary btn-lg">Place Order</button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )
    },
    {
      name: "Standard Tile Components",
      component: (
        <div className="space-y-8">
          {/* Basic Tiles */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Basic Tiles</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {/* Standard Card */}
              <div className="bg-white p-4 rounded-lg shadow border border-gray-200">
                <h4 className="font-semibold text-gray-900 mb-2">Standard Card</h4>
                <p className="text-sm text-gray-600">Basic white card with subtle shadow and border.</p>
              </div>
              
              {/* Primary Card */}
              <div className="bg-primary-50 p-4 rounded-lg border border-primary-200">
                <h4 className="font-semibold text-primary-800 mb-2">Primary Card</h4>
                <p className="text-sm text-primary-700">Card with primary color background and border.</p>
              </div>
              
              {/* Secondary Card */}
              <div className="bg-secondary-50 p-4 rounded-lg border border-secondary-200">
                <h4 className="font-semibold text-secondary-800 mb-2">Secondary Card</h4>
                <p className="text-sm text-secondary-700">Card with secondary color background and border.</p>
              </div>
            </div>
          </div>

          {/* Status Tiles */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Status Tiles</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {/* Success Status */}
              <div className="bg-success-50 p-4 rounded-lg border border-success-200">
                <div className="flex items-center mb-2">
                  <span className="text-success-600 text-xl mr-2">✓</span>
                  <h4 className="font-semibold text-success-800">Success</h4>
                </div>
                <p className="text-sm text-success-700">Operation completed successfully.</p>
              </div>
              
              {/* Warning Status */}
              <div className="bg-warning-50 p-4 rounded-lg border border-warning-200">
                <div className="flex items-center mb-2">
                  <span className="text-warning-600 text-xl mr-2">⚠</span>
                  <h4 className="font-semibold text-warning-800">Warning</h4>
                </div>
                <p className="text-sm text-warning-700">Please review before proceeding.</p>
              </div>
              
              {/* Error Status */}
              <div className="bg-error-50 p-4 rounded-lg border border-error-200">
                <div className="flex items-center mb-2">
                  <span className="text-error-600 text-xl mr-2">✗</span>
                  <h4 className="font-semibold text-error-800">Error</h4>
                </div>
                <p className="text-sm text-error-700">Something went wrong.</p>
              </div>
              
              {/* Info Status */}
              <div className="bg-info-50 p-4 rounded-lg border border-info-200">
                <div className="flex items-center mb-2">
                  <span className="text-info-600 text-xl mr-2">ℹ</span>
                  <h4 className="font-semibold text-info-800">Info</h4>
                </div>
                <p className="text-sm text-info-700">Additional information available.</p>
              </div>
            </div>
          </div>

          {/* Metric Tiles */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Metric Tiles</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {/* Large Metric */}
              <div className="bg-white p-6 rounded-lg shadow border border-gray-200 text-center">
                <div className="text-3xl font-bold text-primary-600 mb-1">1,234</div>
                <div className="text-sm text-gray-600">Total Orders</div>
              </div>
              
              {/* Metric with Icon */}
              <div className="bg-white p-6 rounded-lg shadow border border-gray-200">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-2xl font-bold text-secondary-600">89</div>
                    <div className="text-sm text-gray-600">Active Products</div>
                  </div>
                  <div className="w-12 h-12 bg-secondary-100 rounded-lg flex items-center justify-center">
                    <span className="text-secondary-600 text-xl">🛍️</span>
                  </div>
                </div>
              </div>
              
              {/* Metric with Trend */}
              <div className="bg-white p-6 rounded-lg shadow border border-gray-200">
                <div className="flex items-center justify-between mb-2">
                  <div className="text-sm text-gray-600">Revenue</div>
                  <span className="text-success-600 text-sm font-medium">+12%</span>
                </div>
                <div className="text-2xl font-bold text-gray-900">$12,345</div>
              </div>
              
              {/* Metric with Status Badge */}
              <div className="bg-white p-6 rounded-lg shadow border border-gray-200">
                <div className="flex items-center justify-between mb-2">
                  <div className="text-sm text-gray-600">Volunteers</div>
                  <span className="px-2 py-1 text-xs font-semibold rounded-full bg-success-100 text-success-800">
                    Active
                  </span>
                </div>
                <div className="text-2xl font-bold text-gray-900">45</div>
              </div>
            </div>
          </div>

          {/* Action Tiles */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Action Tiles</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {/* Primary Action */}
              <div className="bg-primary-500 text-white p-6 rounded-lg shadow-lg hover:bg-primary-600 transition-colors cursor-pointer">
                <h4 className="font-semibold text-lg mb-2">Create Order</h4>
                <p className="text-primary-100 text-sm mb-4">Start a new customer order</p>
                <button className="bg-white text-primary-600 px-4 py-2 rounded-lg font-medium hover:bg-gray-100 transition-colors">
                  Get Started
                </button>
              </div>
              
              {/* Secondary Action */}
              <div className="bg-secondary-500 text-white p-6 rounded-lg shadow-lg hover:bg-secondary-600 transition-colors cursor-pointer">
                <h4 className="font-semibold text-lg mb-2">Manage Products</h4>
                <p className="text-secondary-100 text-sm mb-4">Update product catalog</p>
                <button className="bg-white text-secondary-600 px-4 py-2 rounded-lg font-medium hover:bg-gray-100 transition-colors">
                  Manage
                </button>
              </div>
              
              {/* Outline Action */}
              <div className="bg-white border-2 border-primary-500 text-primary-600 p-6 rounded-lg hover:bg-primary-50 transition-colors cursor-pointer">
                <h4 className="font-semibold text-lg mb-2">View Reports</h4>
                <p className="text-gray-600 text-sm mb-4">Access analytics and reports</p>
                <button className="bg-primary-500 text-white px-4 py-2 rounded-lg font-medium hover:bg-primary-600 transition-colors">
                  View Reports
                </button>
              </div>
            </div>
          </div>

          {/* Pricing Tiles */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Pricing Tiles</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {/* Standard Price */}
              <div className="bg-white p-6 rounded-lg shadow border border-gray-200 text-center">
                <h4 className="font-semibold text-gray-900 mb-2">Standard Parcel</h4>
                <div className="text-3xl font-bold text-primary-600 mb-2">$25.00</div>
                <p className="text-sm text-gray-600 mb-4">Perfect for small families</p>
                <button className="w-full btn btn-primary">Select</button>
              </div>
              
              {/* Featured Price */}
              <div className="bg-primary-500 text-white p-6 rounded-lg shadow-lg text-center relative">
                <div className="absolute -top-2 left-1/2 transform -translate-x-1/2">
                  <span className="bg-secondary-500 text-white px-3 py-1 text-xs font-semibold rounded-full">
                    Most Popular
                  </span>
                </div>
                <h4 className="font-semibold text-lg mb-2">Family Parcel</h4>
                <div className="text-3xl font-bold mb-2">$45.00</div>
                <p className="text-primary-100 text-sm mb-4">Ideal for larger families</p>
                <button className="w-full bg-white text-primary-600 px-4 py-2 rounded-lg font-medium hover:bg-gray-100 transition-colors">
                  Select
                </button>
              </div>
              
              {/* Price with Discount */}
              <div className="bg-white p-6 rounded-lg shadow border border-gray-200 text-center">
                <h4 className="font-semibold text-gray-900 mb-2">Premium Parcel</h4>
                <div className="flex items-center justify-center mb-2">
                  <span className="text-lg text-gray-500 line-through mr-2">$60.00</span>
                  <span className="text-3xl font-bold text-primary-600">$50.00</span>
                </div>
                <div className="bg-success-100 text-success-800 text-xs font-semibold px-2 py-1 rounded-full mb-4 inline-block">
                  Save $10
                </div>
                <p className="text-sm text-gray-600 mb-4">Premium quality items</p>
                <button className="w-full btn btn-primary">Select</button>
              </div>
            </div>
          </div>

          {/* Information Tiles */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Information Tiles</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {/* Feature Info */}
              <div className="bg-gradient-to-br from-primary-50 to-primary-100 p-6 rounded-lg border border-primary-200">
                <div className="flex items-center mb-3">
                  <div className="w-10 h-10 bg-primary-500 rounded-lg flex items-center justify-center mr-3">
                    <span className="text-white text-lg">🚚</span>
                  </div>
                  <h4 className="font-semibold text-primary-800">Free Delivery</h4>
                </div>
                <p className="text-sm text-primary-700">Orders over $50 qualify for free delivery within our service area.</p>
              </div>
              
              {/* Process Step */}
              <div className="bg-gradient-to-br from-secondary-50 to-secondary-100 p-6 rounded-lg border border-secondary-200">
                <div className="flex items-center mb-3">
                  <div className="w-8 h-8 bg-secondary-500 text-white rounded-full flex items-center justify-center text-sm font-bold mr-3">
                    1
                  </div>
                  <h4 className="font-semibold text-secondary-800">Choose Items</h4>
                </div>
                <p className="text-sm text-secondary-700">Select your food parcel and any additional items you need.</p>
              </div>
              
              {/* Contact Info */}
              <div className="bg-gradient-to-br from-gray-50 to-gray-100 p-6 rounded-lg border border-gray-200">
                <div className="flex items-center mb-3">
                  <div className="w-10 h-10 bg-gray-600 rounded-lg flex items-center justify-center mr-3">
                    <span className="text-white text-lg">📞</span>
                  </div>
                  <h4 className="font-semibold text-gray-800">Need Help?</h4>
                </div>
                <p className="text-sm text-gray-700 mb-2">Call us at (07) 5555 1234</p>
                <p className="text-sm text-gray-700">Mon-Fri 9AM-5PM</p>
              </div>
            </div>
          </div>

          {/* Compact Tiles */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Compact Tiles</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
              {/* Quick Stat */}
              <div className="bg-white p-3 rounded-lg shadow border border-gray-200 text-center">
                <div className="text-lg font-bold text-primary-600">24</div>
                <div className="text-xs text-gray-600">Hours</div>
              </div>
              
              {/* Status Indicator */}
              <div className="bg-success-100 p-3 rounded-lg border border-success-200 text-center">
                <div className="text-success-600 text-lg">●</div>
                <div className="text-xs text-success-800">Online</div>
              </div>
              
              {/* Quick Action */}
              <div className="bg-primary-100 p-3 rounded-lg border border-primary-200 text-center cursor-pointer hover:bg-primary-200 transition-colors">
                <div className="text-primary-600 text-lg">+</div>
                <div className="text-xs text-primary-800">Add</div>
              </div>
              
              {/* Notification */}
              <div className="bg-warning-100 p-3 rounded-lg border border-warning-200 text-center relative">
                <div className="text-warning-600 text-lg">🔔</div>
                <div className="text-xs text-warning-800">Alert</div>
                <div className="absolute -top-1 -right-1 w-3 h-3 bg-error-500 rounded-full"></div>
              </div>
              
              {/* Progress */}
              <div className="bg-white p-3 rounded-lg shadow border border-gray-200 text-center">
                <div className="text-lg font-bold text-gray-900">75%</div>
                <div className="text-xs text-gray-600">Complete</div>
              </div>
              
              {/* Quick Info */}
              <div className="bg-info-100 p-3 rounded-lg border border-info-200 text-center">
                <div className="text-info-600 text-lg">ℹ</div>
                <div className="text-xs text-info-800">Info</div>
              </div>
            </div>
          </div>
        </div>
      )
    }
  ];

  return (
    <div className={`p-6 space-y-8 ${className}`}>
      <div className="text-center">
        <h1 className="text-3xl font-bold mb-2">{paletteName}</h1>
        <p className="text-secondary-600">Color Palette Testing Component</p>
      </div>

      {/* Color Swatches */}
      <div className="space-y-6">
        <h2 className="text-2xl font-semibold">Color Swatches</h2>
        {colorGroups.map((group) => (
          <div key={group.name} className="space-y-3">
            <h3 className="text-lg font-medium">{group.name}</h3>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
              {group.colors.map((color) => (
                <div key={color.name} className="space-y-2">
                  <div className={`h-20 rounded-lg border ${color.class} flex items-center justify-center font-medium`}>
                    {color.name}
                  </div>
                  <p className="text-sm text-center text-secondary-600">{color.name}</p>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* Component Examples */}
      <div className="space-y-6">
        <h2 className="text-2xl font-semibold">Component Examples</h2>
        {componentExamples.map((example) => (
          <div key={example.name} className="space-y-3">
            <h3 className="text-lg font-medium">{example.name}</h3>
            <div className="p-4 border rounded-lg bg-white">
              {example.component}
            </div>
          </div>
        ))}
      </div>

      {/* Typography Test */}
      <div className="space-y-6">
        <h2 className="text-2xl font-semibold">Typography</h2>
        <div className="space-y-4 p-4 border rounded-lg bg-white">
          <h1 className="text-4xl font-bold">Heading 1 - Large Title</h1>
          <h2 className="text-3xl font-semibold">Heading 2 - Section Title</h2>
          <h3 className="text-2xl font-medium">Heading 3 - Subsection</h3>
          <h4 className="text-xl font-medium">Heading 4 - Card Title</h4>
          <p className="text-base">Body text - This is the standard paragraph text that should be easy to read and have good contrast.</p>
          <p className="text-sm text-secondary-600">Small text - This is smaller text often used for captions or secondary information.</p>
          <a href="#" className="text-primary-600 hover:text-primary-700 underline">Link text - This shows how links appear in the palette.</a>
        </div>
      </div>

      {/* Accessibility Notes */}
      <div className="bg-secondary-50 p-4 rounded-lg">
        <h3 className="text-lg font-medium mb-2">Accessibility Notes</h3>
        <ul className="text-sm space-y-1 text-secondary-700">
          <li>• All text should have a minimum contrast ratio of 4.5:1 for normal text</li>
          <li>• Large text (18pt+) should have a minimum contrast ratio of 3:1</li>
          <li>• Interactive elements should have sufficient contrast</li>
          <li>• Color should not be the only way to convey information</li>
        </ul>
      </div>
    </div>
  );
};

export default ColorPaletteTester; 